import { IconMail, IconPassword, TablerIcon } from '@tabler/icons';
import { MantineColor } from '@mantine/core';

import {
  ApplicationType, EmployeeAuthorizeMethod, IntegrationType, SoftwareLicenseEntitlementsCriteria,
  SoftwareLicensePaymentFrequency, SoftwareLicenseStatus, SoftwareLicensePaymentType, SoftwareLicensePaymentStatus,
} from './models';

export const PROJECT_NAME = 'Efficense';
export const PROJECT_SLUG = 'efficense';
export const BASE_DOMAIN = 'efficense.com';

export const AUTH_LS_USER_KEY = `${PROJECT_SLUG}_auth_user`;

export const APP_CURRENT_PROJECT_ID_LS_KEY = `${PROJECT_SLUG}_app_current_project_id`;
export const APP_CURRENT_PROJECT_NAME_LS_KEY = `${PROJECT_SLUG}_app_current_project_name`;
export const APP_CURRENT_PROJECT_ROLE_LS_KEY = `${PROJECT_SLUG}_app_current_project_role`;

export const PHONE_NUMBER_E164_REGEX = /^\+(?:[0-9] ?){6,14}[0-9]$/;

export const PARAMETER_SPLITTER = '[/@/]';

export const HEADER_HEIGHT = 70;

export const TIMEZONE_HEADER_KEY = 'X-Efficense-Timezone';

export const DEVICE_STORAGE_ALERT_THRESHOLD = 90; // percent

export type ProjectRole = 'owner' | 'member';

export const APPLICATION_TYPES: Record<ApplicationType, string> = {
  desktop: 'Desktop',
  browser: 'Browser',
};

export type OS = 'Windows' | 'MacOS' | 'Linux' | 'Unknown';

export const OPERATING_SYSTEMS: Record<OS, { color: string }> = {
  Windows: { color: '#42a5f5' },
  MacOS: { color: '#ea4971' },
  Linux: { color: '#f5c542' },
  Unknown: { color: '#1c2e39' },
};

export interface EmployeeAuthorizeMethodDetail {
  name: string;
  description: string;
  icon: TablerIcon;
}
export const EMPLOYEE_AUTHORIZE_METHODS: Record<EmployeeAuthorizeMethod, EmployeeAuthorizeMethodDetail> = {
  [EmployeeAuthorizeMethod.EAMEmail]: {
    icon: IconMail,
    name: 'Email Validation',
    description: 'Employee can log in to the agent using a one-time code sent to their email.',
  },
  [EmployeeAuthorizeMethod.EAMPassword]: {
    icon: IconPassword,
    name: 'Password',
    description: 'Employee can log in to the agent using a password.',
  },
};

export interface IntegrationTypeDetail {
  name: string;
  description: string;
  categories: string[];
}
export const INTEGRATION_TYPES: Record<IntegrationType, IntegrationTypeDetail> = {
  [IntegrationType.ITAzureActiveDirectory]: {
    name: 'Azure Active Directory',
    description: 'With Azure Active Directory integration, employees are automatically added to the system and agent authentication processes are centrally managed.',
    categories: ['Identity Management'],
  },
  [IntegrationType.ITGoogleWorkspace]: {
    name: 'Google Workspace',
    description: 'With Google Workspace integration, employees are automatically added to the system and agent authentication processes are centrally managed.',
    categories: ['Identity Management'],
  },
};

export interface SoftwareLicenseStatusDetail {
  name: string;
  color: MantineColor;
}
export const SOFTWARE_LICENSE_STATUSES: Record<SoftwareLicenseStatus, SoftwareLicenseStatusDetail> = {
  [SoftwareLicenseStatus.SLStatusActive]: {
    name: 'Active',
    color: 'green',
  },
  [SoftwareLicenseStatus.SLStatusNotStarted]: {
    name: 'Not Started',
    color: 'indigo',
  },
  [SoftwareLicenseStatus.SLStatusExpired]: {
    name: 'Expired',
    color: 'red',
  },
  [SoftwareLicenseStatus.SLStatusTerminated]: {
    name: 'Terminated',
    color: 'orange',
  },
};

export const SOFTWARE_LICENSE_PAYMENT_FREQUENCIES: Record<SoftwareLicensePaymentFrequency, string> = {
  [SoftwareLicensePaymentFrequency.SLPFNone]: 'None',
  [SoftwareLicensePaymentFrequency.SLPFCustom]: 'Custom',
  [SoftwareLicensePaymentFrequency.SLPFDaily]: 'Daily',
  [SoftwareLicensePaymentFrequency.SLPFWeekly]: 'Weekly',
  [SoftwareLicensePaymentFrequency.SLPFMonthly]: 'Monthly',
  [SoftwareLicensePaymentFrequency.SLPFQuarterly]: 'Quarterly',
  [SoftwareLicensePaymentFrequency.SLPFYearly]: 'Yearly',
};

export const SOFTWARE_LICENSE_PAYMENT_TYPES: Record<SoftwareLicensePaymentType, string> = {
  [SoftwareLicensePaymentType.SLPTManual]: 'Manual',
  [SoftwareLicensePaymentType.SLPTAuto]: 'Auto',
};

export interface SoftwareLicensePaymentStatusDetail {
  name: string;
  color: MantineColor;
}
export const SOFTWARE_LICENSE_PAYMENT_STATUSES: Record<SoftwareLicensePaymentStatus, SoftwareLicensePaymentStatusDetail> = {
  [SoftwareLicensePaymentStatus.SLPSVoided]: {
    name: 'Voided',
    color: 'red',
  },
  [SoftwareLicensePaymentStatus.SLPSUnpaid]: {
    name: 'Unpaid',
    color: 'orange',
  },
  [SoftwareLicensePaymentStatus.SLPSPaid]: {
    name: 'Paid',
    color: 'green',
  },
  [SoftwareLicensePaymentStatus.SLPSUpcoming]: {
    name: 'Upcoming',
    color: 'blue',
  },
};

export const SOFTWARE_LICENSE_CUSTOM_PAYMENT_FREQUENCY_OPTIONS = [
  { value: 'd', label: 'Days' },
  { value: 'w', label: 'Weeks' },
  { value: 'm', label: 'Months' },
  { value: 'y', label: 'Years' },
];

export interface SoftwareLicenseEntitlementsCriteriaDetail {
  name: string;
  description: string;
}
export const SOFTWARE_LICENSE_ENTITLEMENTS_CRITERIA: Record<SoftwareLicenseEntitlementsCriteria, SoftwareLicenseEntitlementsCriteriaDetail> = {
  [SoftwareLicenseEntitlementsCriteria.SLECNone]: {
    name: 'No Entitlements',
    description: 'This license does not require any assignment to users or devices.',
  },
  [SoftwareLicenseEntitlementsCriteria.SLECUserBased]: {
    name: 'User Based',
    description: 'This license will be assigned to specific users who need access to the software.',
  },
  [SoftwareLicenseEntitlementsCriteria.SLECDeviceBased]: {
    name: 'Device Based',
    description: 'This license will be assigned to specific devices where the software needs to be installed.',
  },
  [SoftwareLicenseEntitlementsCriteria.SLECUserDeviceBased]: {
    name: 'User & Device Based',
    description: 'This license will be assigned to both specific users and the devices they use to access the software.',
  },
};

export const SOFTWARE_LICENSE_VERSION_CONSTRAINT_OPTIONS = [
  { value: '>=', label: 'Greater than or equal to' },
  { value: '<=', label: 'Less than or equal to' },
  { value: '=', label: 'Equal to' },
];

export const SOFTWARE_LICENSE_VERSION_CONSTRAINT_REGEX = /^(<=|>=|<|>|=)(.+)/;
